import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { ArrowLeft, RefreshCw, AlertTriangle, Clock, History, Diamond } from 'lucide-react';

const PRIORITY_CONFIG = {
  critical: { label: 'Critical', color: 'bg-red-500', textColor: 'text-red-700', bgLight: 'bg-red-50 border-red-200', dot: 'bg-red-500' },
  high: { label: 'High', color: 'bg-orange-500', textColor: 'text-orange-700', bgLight: 'bg-orange-50 border-orange-200', dot: 'bg-orange-500' },
  normal: { label: 'Normal', color: 'bg-yellow-500', textColor: 'text-yellow-700', bgLight: 'bg-yellow-50 border-yellow-200', dot: 'bg-yellow-500' },
  low: { label: 'Low', color: 'bg-blue-500', textColor: 'text-blue-700', bgLight: 'bg-blue-50 border-blue-200', dot: 'bg-blue-500' },
};

const PriorityBadge = ({ priority }) => {
  const config = PRIORITY_CONFIG[priority] || PRIORITY_CONFIG.normal;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${config.bgLight} ${config.textColor} border`} data-testid={`priority-badge-${priority}`}>
      <span className={`w-2 h-2 rounded-full ${config.dot}`} />
      {config.label}
    </span>
  );
};

const DiamondQueue = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [queueData, setQueueData] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [priorityDialogOpen, setPriorityDialogOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [newPriority, setNewPriority] = useState('');
  const [priorityReason, setPriorityReason] = useState('');
  const [reasons, setReasons] = useState([]);
  const [updating, setUpdating] = useState(false);
  const [auditLogOpen, setAuditLogOpen] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);
  const [filterPriority, setFilterPriority] = useState('all');

  const canChangePriority = currentUser?.role === 'admin' || currentUser?.role === 'production_supervisor';

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [queueRes, dashRes, reasonsRes] = await Promise.all([
        api.get('/diamond-queue'),
        canChangePriority ? api.get('/supervisor-dashboard') : Promise.resolve({ data: null }),
        api.get('/priority-reasons'),
      ]);
      setQueueData(queueRes.data);
      setDashboardData(dashRes.data);
      setReasons(reasonsRes.data);
    } catch (error) {
      console.error('Error fetching queue:', error);
    } finally {
      setLoading(false);
    }
  }, [canChangePriority]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handlePriorityChange = async () => {
    if (!selectedRequest || !newPriority || !priorityReason) return;
    setUpdating(true);
    try {
      await api.patch(`/material-requests/${selectedRequest._id}/priority`, {
        priority: newPriority,
        reason: priorityReason,
      });
      setPriorityDialogOpen(false);
      setSelectedRequest(null);
      setNewPriority('');
      setPriorityReason('');
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to update priority');
    } finally {
      setUpdating(false);
    }
  };

  const openPriorityDialog = (req) => {
    setSelectedRequest(req);
    setNewPriority(req.priority || 'normal');
    setPriorityReason('');
    setPriorityDialogOpen(true);
  };

  const openAuditLog = async (requestId) => {
    try {
      const res = await api.get(`/priority-audit-log?material_request_id=${requestId}`);
      setAuditLogs(res.data);
      setAuditLogOpen(true);
    } catch (error) {
      console.error('Error fetching audit log:', error);
    }
  };

  const filteredQueue = queueData?.queue?.filter(r => {
    if (filterPriority === 'all') return true;
    return (r.priority || 'normal') === filterPriority;
  }) || [];

  const isNearDeadline = (deliveryDate) => {
    if (!deliveryDate) return false;
    const d = new Date(deliveryDate);
    const now = new Date();
    const diff = (d - now) / (1000 * 60 * 60 * 24);
    return diff >= 0 && diff <= 2;
  };

  const isOverdue = (deliveryDate, status) => {
    if (!deliveryDate || ['Completed', 'Delivered', 'Cancelled'].includes(status)) return false;
    return new Date(deliveryDate) < new Date();
  };

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin text-emerald-600 mx-auto mb-2" />
          <p className="text-gray-500">Loading diamond queue...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-gray-50" data-testid="diamond-queue-page">
      {/* Header */}
      <div className="bg-[#075E54] text-white px-4 py-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="text-white hover:bg-emerald-700" onClick={() => navigate(-1)} data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-lg font-semibold flex items-center gap-2">
                <Diamond className="w-5 h-5" /> Diamond Queue
              </h1>
              <p className="text-xs text-emerald-200">{queueData?.total_tasks || 0} pending diamond requests</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Select value={filterPriority} onValueChange={setFilterPriority}>
              <SelectTrigger className="w-32 h-8 bg-emerald-700 border-emerald-600 text-white text-xs" data-testid="filter-priority-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Priorities</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="normal">Normal</SelectItem>
                <SelectItem value="low">Low</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="ghost" size="icon" className="text-white hover:bg-emerald-700" onClick={fetchData} data-testid="refresh-btn">
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Warnings */}
      {queueData?.warnings?.length > 0 && (
        <div className="px-4 pt-3 space-y-2" data-testid="queue-warnings">
          {queueData.warnings.map((w, i) => (
            <div key={i} className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-sm text-amber-800">
              <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0" />
              <span>{w}</span>
            </div>
          ))}
        </div>
      )}

      {/* Supervisor Stats Cards */}
      {canChangePriority && dashboardData && (
        <div className="px-4 pt-3">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="supervisor-stats">
            <div className="bg-white rounded-lg border p-3">
              <p className="text-xs text-gray-500 font-medium">Critical</p>
              <p className="text-2xl font-bold text-red-600">{dashboardData.priority_counts?.critical || 0}</p>
              <p className="text-xs text-gray-400">{dashboardData.critical_percentage}% of total</p>
            </div>
            <div className="bg-white rounded-lg border p-3">
              <p className="text-xs text-gray-500 font-medium">Nearing Deadline</p>
              <p className="text-2xl font-bold text-orange-600">{dashboardData.nearing_deadline || 0}</p>
              <p className="text-xs text-gray-400">within 2 days</p>
            </div>
            <div className="bg-white rounded-lg border p-3">
              <p className="text-xs text-gray-500 font-medium">Overdue</p>
              <p className="text-2xl font-bold text-red-500">{dashboardData.overdue || 0}</p>
              <p className="text-xs text-gray-400">past deadline</p>
            </div>
            <div className="bg-white rounded-lg border p-3">
              <p className="text-xs text-gray-500 font-medium">Re-prioritized Today</p>
              <p className="text-2xl font-bold text-blue-600">{dashboardData.reprioritized_today || 0}</p>
              <p className="text-xs text-gray-400">priority changes</p>
            </div>
          </div>
        </div>
      )}

      {/* Priority Distribution Bar */}
      {queueData && queueData.total_tasks > 0 && (
        <div className="px-4 pt-3">
          <div className="bg-white rounded-lg border p-3">
            <p className="text-xs text-gray-500 font-medium mb-2">Priority Distribution</p>
            <div className="flex h-3 rounded-full overflow-hidden gap-0.5">
              {['critical', 'high', 'normal', 'low'].map(p => {
                const count = queueData.priority_counts?.[p] || 0;
                const pct = (count / queueData.total_tasks) * 100;
                if (pct === 0) return null;
                return (
                  <div key={p} className={`${PRIORITY_CONFIG[p].color} transition-all`} style={{ width: `${pct}%` }} title={`${PRIORITY_CONFIG[p].label}: ${count}`} />
                );
              })}
            </div>
            <div className="flex justify-between mt-2 text-xs text-gray-500">
              {['critical', 'high', 'normal', 'low'].map(p => (
                <span key={p} className="flex items-center gap-1">
                  <span className={`w-2 h-2 rounded-full ${PRIORITY_CONFIG[p].dot}`} />
                  {PRIORITY_CONFIG[p].label}: {queueData.priority_counts?.[p] || 0}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Queue List */}
      <div className="flex-1 overflow-y-auto px-4 pt-3 pb-4 space-y-2" data-testid="queue-list">
        {filteredQueue.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <Diamond className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p className="font-medium">No diamond requests in queue</p>
            <p className="text-sm mt-1">Diamond material requests will appear here when created</p>
          </div>
        ) : (
          filteredQueue.map((req, index) => {
            const priority = req.priority || 'normal';
            const nearDeadline = isNearDeadline(req.delivery_date);
            const overdue = isOverdue(req.delivery_date, req.order_status);
            return (
              <div
                key={req._id}
                className={`bg-white rounded-lg border-l-4 shadow-sm hover:shadow transition-shadow ${
                  priority === 'critical' ? 'border-l-red-500' :
                  priority === 'high' ? 'border-l-orange-500' :
                  priority === 'low' ? 'border-l-blue-500' : 'border-l-yellow-500'
                }`}
                data-testid={`queue-item-${index}`}
              >
                <div className="p-3">
                  {/* Top row */}
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-bold text-gray-400 w-6">#{index + 1}</span>
                      <span className="font-semibold text-sm text-gray-900">{req.request_number}</span>
                      <PriorityBadge priority={priority} />
                      <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                        req.status === 'pending' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'
                      }`}>
                        {req.status}
                      </span>
                      {overdue && (
                        <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700 border border-red-200">OVERDUE</span>
                      )}
                      {nearDeadline && !overdue && (
                        <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-700 border border-amber-200">DUE SOON</span>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      {canChangePriority && (
                        <>
                          <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => openAuditLog(req._id)} data-testid={`audit-btn-${index}`}>
                            <History className="w-3 h-3 mr-1" /> Log
                          </Button>
                          <Button variant="outline" size="sm" className="h-7 px-2 text-xs" onClick={() => openPriorityDialog(req)} data-testid={`change-priority-btn-${index}`}>
                            Change Priority
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                  {/* Details row */}
                  <div className="flex items-center justify-between text-xs text-gray-500 ml-8">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-gray-700">Order: {req.order_number}</span>
                      {req.order_title && <span>| {req.order_title}</span>}
                      {req.customer_name && <span>| {req.customer_name}</span>}
                      {req.worker_name && <span className="text-emerald-600">Worker: {req.worker_name}</span>}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {req.weight_carats && <span className="font-medium">{req.weight_carats} cts</span>}
                      {req.delivery_date && (
                        <span className={`${overdue ? 'text-red-600 font-semibold' : nearDeadline ? 'text-amber-600' : ''}`}>
                          Due: {new Date(req.delivery_date).toLocaleDateString('en-IN')}
                        </span>
                      )}
                    </div>
                  </div>
                  {req.comments && (
                    <p className="text-xs text-gray-400 ml-8 mt-1 italic">"{req.comments}"</p>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Priority Change Dialog */}
      <Dialog open={priorityDialogOpen} onOpenChange={setPriorityDialogOpen}>
        <DialogContent className="max-w-md" data-testid="priority-change-dialog">
          <DialogHeader>
            <DialogTitle>Change Priority - {selectedRequest?.request_number}</DialogTitle>
            <p className="text-sm text-gray-500">Order: {selectedRequest?.order_number} | Select new priority and reason</p>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">Current Priority</label>
              <PriorityBadge priority={selectedRequest?.priority || 'normal'} />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">New Priority *</label>
              <Select value={newPriority} onValueChange={setNewPriority}>
                <SelectTrigger data-testid="new-priority-select">
                  <SelectValue placeholder="Select priority" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="critical">Critical - Urgent delivery / VIP</SelectItem>
                  <SelectItem value="high">High - Delivery within 1-2 days</SelectItem>
                  <SelectItem value="normal">Normal - Standard workflow</SelectItem>
                  <SelectItem value="low">Low - Stock / non-urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">Reason *</label>
              <Select value={priorityReason} onValueChange={setPriorityReason}>
                <SelectTrigger data-testid="priority-reason-select">
                  <SelectValue placeholder="Select reason" />
                </SelectTrigger>
                <SelectContent>
                  {reasons.map((r) => (
                    <SelectItem key={r} value={r}>{r}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPriorityDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handlePriorityChange}
              disabled={updating || !newPriority || !priorityReason || newPriority === (selectedRequest?.priority || 'normal')}
              className="bg-[#075E54] hover:bg-[#064E46]"
              data-testid="confirm-priority-change-btn"
            >
              {updating ? 'Updating...' : 'Update Priority'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Audit Log Dialog */}
      <Dialog open={auditLogOpen} onOpenChange={setAuditLogOpen}>
        <DialogContent className="max-w-lg max-h-[70vh] overflow-y-auto" data-testid="audit-log-dialog">
          <DialogHeader>
            <DialogTitle>Priority Change History</DialogTitle>
            <p className="text-sm text-gray-500">Track all priority changes for this diamond request</p>
          </DialogHeader>
          {auditLogs.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-4">No priority changes recorded</p>
          ) : (
            <div className="space-y-3">
              {auditLogs.map((log, i) => (
                <div key={i} className="border rounded-lg p-3 text-sm">
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <PriorityBadge priority={log.from_priority} />
                      <span className="text-gray-400">&rarr;</span>
                      <PriorityBadge priority={log.to_priority} />
                    </div>
                    <span className="text-xs text-gray-400">
                      {new Date(log.timestamp).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}
                    </span>
                  </div>
                  <p className="text-xs text-gray-600">By: {log.changed_by_name}</p>
                  <p className="text-xs text-gray-500">Reason: {log.reason}</p>
                  {log.order_number && <p className="text-xs text-gray-400">Order: {log.order_number}</p>}
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DiamondQueue;
